import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Circle,
  ClipboardCheck,
  Database,
  FileText,
  GitBranch,
  Save,
  Search,
  Send,
  ShieldCheck,
  Users,
  Wrench,
  X,
} from 'lucide-react';
import api from '../../services/api';
import { Aprovador, CategoriaCorrecao, CorrecaoGMUD, CreateGMUDData, GMUD } from '../../types';
import { normalizeDate } from '../../utils/dateUtils';
import FileUpload from '../FileUpload';
import TempFileUpload from '../TempFileUpload';

// ---------------------------------------------------------------------------
// Tipos públicos
// ---------------------------------------------------------------------------

export interface ArquivosPendentes {
  evidencia: File[];
  script: File[];
}

export interface GMUDFormProps {
  modo: 'criar' | 'editar';
  gmud?: GMUD;
  aprovadores: Aprovador[];
  // Criação: responsável fixo (usuário logado), somente leitura
  responsavelFixo?: string;
  salvando: boolean;
  // Correção solicitada em aberto: destaca as seções que precisam de ajuste
  correcao?: CorrecaoGMUD | null;
  onSalvar: (dados: CreateGMUDData, arquivos: ArquivosPendentes, enviar: boolean) => Promise<void>;
  onCancelar: () => void;
}

type SecaoId = 'oque' | 'quando' | 'como' | 'seguranca' | 'revisao';
type SituacaoSecao = 'ok' | 'pendente' | 'erro' | 'opcional';

const JIRA_REGEX = /^[A-Za-z][A-Za-z0-9_]+-\d+$/;

const SECOES: { id: SecaoId; titulo: string; pergunta: string; icone: React.ElementType }[] = [
  { id: 'oque', titulo: 'O quê', pergunta: 'O que vai mudar?', icone: FileText },
  { id: 'quando', titulo: 'Quando e quem', pergunta: 'Quando será feito e quem aprova?', icone: Users },
  { id: 'como', titulo: 'Como', pergunta: 'Como será executado?', icone: Wrench },
  {
    id: 'seguranca',
    titulo: 'Segurança',
    pergunta: 'Como voltar atrás e como foi validado?',
    icone: ShieldCheck,
  },
  { id: 'revisao', titulo: 'Revisão', pergunta: 'Está pronta para seguir?', icone: ClipboardCheck },
];

// Seções a destacar para cada área citada no pedido de correção
const SECOES_POR_CATEGORIA: Record<CategoriaCorrecao, SecaoId[]> = {
  informacoes: ['oque', 'quando', 'seguranca'],
  script: ['como'],
  versao: ['como'],
  outro: [],
};

const OPCOES_IMPACTO = [
  { valor: 'baixo', rotulo: 'Baixo', cor: 'border-success-300 bg-success-50 text-success-800' },
  { valor: 'medio', rotulo: 'Médio', cor: 'border-primary-300 bg-primary-50 text-primary-800' },
  { valor: 'alto', rotulo: 'Alto', cor: 'border-warning-300 bg-warning-50 text-warning-800' },
  { valor: 'critico', rotulo: 'Crítico', cor: 'border-danger-300 bg-danger-50 text-danger-800' },
] as const;

const OPCOES_TIPO = [
  {
    valor: 'normal',
    rotulo: 'Normal',
    ajuda: 'Mudança planejada, segue o fluxo padrão',
    cor: 'border-primary-300 bg-primary-50 text-primary-800',
  },
  {
    valor: 'emergencial',
    rotulo: 'Emergencial',
    ajuda: 'Mudança urgente, requer atenção imediata',
    cor: 'border-danger-300 bg-danger-50 text-danger-800',
  },
] as const;

// ---------------------------------------------------------------------------
// Valores iniciais
// ---------------------------------------------------------------------------

const valoresIniciais = (
  modo: 'criar' | 'editar',
  gmud: GMUD | undefined,
  responsavelFixo: string | undefined
): CreateGMUDData => {
  if (modo === 'editar' && gmud) {
    return {
      titulo: gmud.titulo || '',
      jira_chamado: gmud.jira_chamado || '',
      descricao: gmud.descricao || '',
      impacto_risco: gmud.impacto_risco || 'medio',
      responsavel: gmud.responsavel || '',
      data_prevista: normalizeDate(gmud.data_prevista),
      tempo_execucao: gmud.tempo_execucao || '',
      aprovadores: (gmud.aprovadores || []).map(item => Number(item.aprovador_id)),
      evidencia_testes: gmud.evidencia_testes || '',
      evidencia_testes_link: gmud.evidencia_testes_link || '',
      tipo_gmud: gmud.tipo_gmud === 'emergencial' ? 'emergencial' : 'normal',
      pipeline_necessario:
        gmud.pipeline_necessario === true || Number(gmud.pipeline_necessario) === 1,
      nome_pipeline: gmud.nome_pipeline || '',
      versao_pipeline: gmud.versao_pipeline || '',
      script_banco: gmud.script_banco || '',
      plano_rollback: gmud.plano_rollback || '',
    };
  }

  return {
    titulo: '',
    jira_chamado: '',
    descricao: '',
    impacto_risco: 'medio',
    responsavel: responsavelFixo || '',
    data_prevista: '',
    tempo_execucao: '',
    aprovadores: [],
    evidencia_testes: '',
    evidencia_testes_link: '',
    tipo_gmud: 'normal',
    pipeline_necessario: false,
    nome_pipeline: '',
    versao_pipeline: '',
    script_banco: '',
    plano_rollback: '',
  };
};

// ---------------------------------------------------------------------------
// Blocos de apresentação
// ---------------------------------------------------------------------------

const Rotulo: React.FC<{
  children: React.ReactNode;
  obrigatorio?: boolean;
  paraEnviar?: boolean;
  htmlFor?: string;
}> = ({ children, obrigatorio, paraEnviar, htmlFor }) => (
  <label htmlFor={htmlFor} className='mb-1.5 flex flex-wrap items-center gap-2 text-sm font-medium text-gray-700'>
    <span>
      {children}
      {obrigatorio && <span className='ml-0.5 text-danger-600'>*</span>}
    </span>
    {paraEnviar && (
      <span className='rounded bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-700'>
        Obrigatório para enviar
      </span>
    )}
  </label>
);

const MensagemErro: React.FC<{ mensagem?: string }> = ({ mensagem }) =>
  mensagem ? <p className='mt-1 text-sm text-danger-600'>{mensagem}</p> : null;

const Ajuda: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className='mt-1 text-xs text-gray-500'>{children}</p>
);

const Interruptor: React.FC<{
  checked: boolean;
  onChange: (valor: boolean) => void;
  titulo: string;
  descricao?: string;
  icone?: React.ElementType;
}> = ({ checked, onChange, titulo, descricao, icone: Icone }) => (
  <label className='flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50'>
    <span className='relative mt-0.5 inline-flex h-5 w-9 flex-shrink-0'>
      <input
        type='checkbox'
        role='switch'
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className='peer sr-only'
      />
      <span className='absolute inset-0 rounded-full bg-gray-300 transition-colors peer-checked:bg-primary-600 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2' />
      <span className='absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4' />
    </span>
    <span className='min-w-0'>
      <span className='flex items-center gap-1.5 text-sm font-medium text-gray-900'>
        {Icone && <Icone className='h-4 w-4 text-gray-500' />}
        {titulo}
      </span>
      {descricao && <span className='block text-xs text-gray-500'>{descricao}</span>}
    </span>
  </label>
);

interface SecaoProps {
  id: SecaoId;
  numero: number;
  destaque: boolean;
  children: React.ReactNode;
}

const Secao: React.FC<SecaoProps> = ({ id, numero, destaque, children }) => {
  const info = SECOES.find(item => item.id === id) || SECOES[0];
  const Icone = info.icone;
  return (
    <section
      id={`secao-${id}`}
      aria-labelledby={`titulo-${id}`}
      className={`card scroll-mt-24 ${destaque ? 'ring-2 ring-orange-300' : ''}`}
    >
      <header className='mb-5 flex items-start gap-3'>
        <span className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700'>
          {numero}
        </span>
        <div className='min-w-0 flex-1'>
          <h2 id={`titulo-${id}`} className='flex items-center gap-2 text-lg font-semibold text-gray-900'>
            <Icone className='h-5 w-5 text-gray-500' />
            {info.pergunta}
          </h2>
          {destaque && (
            <p className='mt-1 inline-flex items-center gap-1 rounded bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800'>
              <AlertCircle className='h-3.5 w-3.5' />
              Ponto citado na correção solicitada
            </p>
          )}
        </div>
      </header>
      <div className='space-y-5'>{children}</div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Formulário
// ---------------------------------------------------------------------------

const GMUDForm: React.FC<GMUDFormProps> = ({
  modo,
  gmud,
  aprovadores,
  responsavelFixo,
  salvando,
  correcao,
  onSalvar,
  onCancelar,
}) => {
  const editando = modo === 'editar';
  const gmudId = gmud?.id;
  const podeEnviarAprovacao = !editando || gmud?.status === 'rascunho';
  const emCorrecao = editando && gmud?.status === 'correcao_solicitada';

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isDirty },
  } = useForm<CreateGMUDData>({
    mode: 'onChange',
    defaultValues: valoresIniciais(modo, gmud, responsavelFixo),
  });

  const valores = watch();

  // Arquivos: na criação ficam em memória até salvar; na edição já estão no servidor
  const [arquivosEvidencia, setArquivosEvidencia] = useState<File[]>([]);
  const [arquivosScript, setArquivosScript] = useState<File[]>([]);
  const [qtdEvidenciaServidor, setQtdEvidenciaServidor] = useState(0);
  const [qtdScriptServidor, setQtdScriptServidor] = useState(0);

  const qtdEvidencias = editando ? qtdEvidenciaServidor : arquivosEvidencia.length;
  const qtdScript = editando ? qtdScriptServidor : arquivosScript.length;

  const [temScript, setTemScript] = useState(!!(gmud?.script_banco || '').trim());
  const [busca, setBusca] = useState('');
  const [secaoAtiva, setSecaoAtiva] = useState<SecaoId>('oque');

  // Na edição, o script pode existir só como anexo: abre o bloco se houver arquivos
  useEffect(() => {
    if (!editando || !gmudId) return;
    let cancelado = false;
    api
      .get(`/upload/evidencias/${gmudId}`, { params: { categoria: 'script_banco' } })
      .then(resposta => {
        if (cancelado) return;
        const quantidade = Array.isArray(resposta.data) ? resposta.data.length : 0;
        setQtdScriptServidor(quantidade);
        if (quantidade > 0) setTemScript(true);
      })
      .catch(() => undefined);
    return () => {
      cancelado = true;
    };
  }, [editando, gmudId]);

  // Avisa ao tentar fechar a aba com alterações não salvas
  useEffect(() => {
    if (!isDirty || salvando) return;
    const aviso = (evento: BeforeUnloadEvent) => {
      evento.preventDefault();
      evento.returnValue = '';
    };
    window.addEventListener('beforeunload', aviso);
    return () => window.removeEventListener('beforeunload', aviso);
  }, [isDirty, salvando]);

  // Índice: destaca a seção visível na tela
  useEffect(() => {
    const elementos = SECOES.map(item => document.getElementById(`secao-${item.id}`)).filter(
      (item): item is HTMLElement => !!item
    );
    if (elementos.length === 0 || typeof IntersectionObserver === 'undefined') return;

    const observador = new IntersectionObserver(
      entradas => {
        const visivel = entradas
          .filter(entrada => entrada.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visivel) setSecaoAtiva(visivel.target.id.replace('secao-', '') as SecaoId);
      },
      { rootMargin: '-15% 0px -70% 0px' }
    );
    elementos.forEach(elemento => observador.observe(elemento));
    return () => observador.disconnect();
  }, []);

  const irParaSecao = useCallback((id: SecaoId) => {
    setSecaoAtiva(id);
    document.getElementById(`secao-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // -------------------------------------------------------------------------
  // Prontidão para enviar (mesma regra do servidor + campos básicos)
  // -------------------------------------------------------------------------
  const jiraValido = JIRA_REGEX.test((valores.jira_chamado || '').trim());
  const qtdAprovadores = (valores.aprovadores || []).length;

  const itensProntidao = useMemo(
    () => [
      {
        chave: 'titulo',
        rotulo: 'Título informado',
        ok: !!(valores.titulo || '').trim(),
        secao: 'oque' as SecaoId,
      },
      { chave: 'jira', rotulo: 'Chamado Jira válido', ok: jiraValido, secao: 'oque' as SecaoId },
      {
        chave: 'data',
        rotulo: 'Data prevista',
        ok: !!valores.data_prevista,
        secao: 'quando' as SecaoId,
      },
      {
        chave: 'aprovadores',
        rotulo: 'Ao menos um aprovador',
        ok: qtdAprovadores > 0,
        secao: 'quando' as SecaoId,
      },
      {
        chave: 'rollback',
        rotulo: 'Plano de rollback',
        ok: !!(valores.plano_rollback || '').trim(),
        secao: 'seguranca' as SecaoId,
      },
      {
        chave: 'evidencia',
        rotulo: 'Descrição das evidências de testes',
        ok: !!(valores.evidencia_testes || '').trim(),
        secao: 'seguranca' as SecaoId,
      },
      {
        chave: 'arquivo',
        rotulo: 'Ao menos um arquivo de evidência',
        ok: qtdEvidencias > 0,
        secao: 'seguranca' as SecaoId,
      },
    ],
    [
      valores.titulo,
      jiraValido,
      valores.data_prevista,
      qtdAprovadores,
      valores.plano_rollback,
      valores.evidencia_testes,
      qtdEvidencias,
    ]
  );

  const faltantes = itensProntidao.filter(item => !item.ok);
  const prontoParaEnviar = faltantes.length === 0;

  const situacaoSecao = (id: SecaoId): SituacaoSecao => {
    const itens = itensProntidao.filter(item => item.secao === id);
    const temErro =
      (id === 'oque' && (errors.titulo || errors.jira_chamado)) ||
      (id === 'quando' && (errors.data_prevista || errors.aprovadores || errors.responsavel));
    if (temErro) return 'erro';
    if (id === 'como') {
      const preenchido =
        valores.pipeline_necessario ||
        !!(valores.script_banco || '').trim() ||
        qtdScript > 0;
      return preenchido ? 'ok' : 'opcional';
    }
    if (id === 'revisao') return prontoParaEnviar ? 'ok' : 'pendente';
    return itens.every(item => item.ok) ? 'ok' : 'pendente';
  };

  const secoesDestacadas = useMemo(() => {
    const conjunto = new Set<SecaoId>();
    (correcao?.categorias_lista || []).forEach(categoria =>
      (SECOES_POR_CATEGORIA[categoria] || []).forEach(secao => conjunto.add(secao))
    );
    return conjunto;
  }, [correcao]);

  // -------------------------------------------------------------------------
  // Envio
  // -------------------------------------------------------------------------
  const dadosLimpos = (dados: CreateGMUDData): CreateGMUDData => ({
    ...dados,
    jira_chamado: (dados.jira_chamado || '').trim().toUpperCase(),
    aprovadores: (dados.aprovadores || []).map(item => Number(item)),
  });

  const enviar = (comEnvio: boolean) =>
    handleSubmit(
      async dados => {
        if (comEnvio && !prontoParaEnviar) {
          toast.error(`Ainda falta: ${faltantes.map(item => item.rotulo).join(', ')}`);
          irParaSecao(faltantes[0].secao);
          return;
        }
        await onSalvar(
          dadosLimpos(dados),
          { evidencia: arquivosEvidencia, script: arquivosScript },
          comEnvio
        );
      },
      invalidos => {
        toast.error('Revise os campos destacados antes de salvar');
        const alvo: SecaoId = invalidos.aprovadores || invalidos.data_prevista || invalidos.responsavel
          ? 'quando'
          : 'oque';
        irParaSecao(alvo);
      }
    );

  const rotuloSalvar = !editando
    ? 'Salvar rascunho'
    : emCorrecao
    ? 'Salvar correções'
    : podeEnviarAprovacao
    ? 'Salvar rascunho'
    : 'Salvar alterações';

  const alternarScript = (ligar: boolean) => {
    if (!ligar && ((valores.script_banco || '').trim() || qtdScript > 0)) {
      toast.error('Remova o script e os anexos antes de desativar esta opção');
      return;
    }
    setTemScript(ligar);
  };

  // -------------------------------------------------------------------------
  // Aprovadores (busca + seleção)
  // -------------------------------------------------------------------------
  // Inclui aprovadores já vinculados à GMUD que não vieram na lista (ex.: desativados depois)
  const todosAprovadores = useMemo(() => {
    const lista = [...aprovadores];
    (gmud?.aprovadores || []).forEach(vinculo => {
      if (!lista.some(item => Number(item.id) === Number(vinculo.aprovador_id))) {
        lista.push({
          id: Number(vinculo.aprovador_id),
          nome: vinculo.aprovador_nome || `Aprovador #${vinculo.aprovador_id}`,
          email: vinculo.aprovador_email || '',
          cargo: vinculo.aprovador_cargo,
          ativo: false,
          perfil_master: false,
          created_at: '',
          updated_at: '',
        });
      }
    });
    return lista;
  }, [aprovadores, gmud]);

  const aprovadoresFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return todosAprovadores;
    return todosAprovadores.filter(item =>
      `${item.nome} ${item.cargo || ''} ${item.email}`.toLowerCase().includes(termo)
    );
  }, [todosAprovadores, busca]);

  const classeCampo = (temErro: boolean) => `input ${temErro ? 'input-error' : ''}`;

  return (
    <form
      onSubmit={enviar(false)}
      noValidate
      className='mx-auto grid max-w-6xl grid-cols-1 gap-6 lg:grid-cols-[13rem_minmax(0,1fr)]'
    >
      {/* Índice: barra no topo no mobile, lateral fixa no desktop */}
      <nav
        aria-label='Seções do formulário'
        className='sticky top-0 z-20 -mx-4 border-b border-gray-200 bg-white/95 px-4 py-2 backdrop-blur sm:mx-0 sm:rounded-lg sm:border sm:px-2 lg:top-6 lg:self-start lg:py-3'
      >
        <ol className='flex gap-1 overflow-x-auto lg:flex-col'>
          {SECOES.map((secao, indice) => {
            const situacao = situacaoSecao(secao.id);
            const ativa = secaoAtiva === secao.id;
            return (
              <li key={secao.id} className='flex-shrink-0'>
                <button
                  type='button'
                  onClick={() => irParaSecao(secao.id)}
                  aria-current={ativa ? 'step' : undefined}
                  className={`flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors ${
                    ativa ? 'bg-primary-50 font-semibold text-primary-800' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {situacao === 'ok' && <CheckCircle2 className='h-4 w-4 flex-shrink-0 text-success-600' />}
                  {situacao === 'erro' && <AlertCircle className='h-4 w-4 flex-shrink-0 text-danger-600' />}
                  {situacao === 'pendente' && <Circle className='h-4 w-4 flex-shrink-0 text-amber-500' />}
                  {situacao === 'opcional' && <Circle className='h-4 w-4 flex-shrink-0 text-gray-300' />}
                  <span className='whitespace-nowrap'>
                    {indice + 1}. {secao.titulo}
                  </span>
                  {secoesDestacadas.has(secao.id) && (
                    <span className='ml-auto h-2 w-2 flex-shrink-0 rounded-full bg-orange-500' title='Correção solicitada' />
                  )}
                </button>
              </li>
            );
          })}
        </ol>
        <p className='mt-2 hidden px-2.5 text-xs text-gray-500 lg:block'>
          {prontoParaEnviar
            ? 'Tudo pronto para enviar.'
            : `Faltam ${faltantes.length} item(ns) para enviar à aprovação.`}
        </p>
      </nav>

      <div className='min-w-0 space-y-6'>
        {/* Correção solicitada: contexto do que precisa ser ajustado */}
        {emCorrecao && (
          <div className='rounded-lg border border-orange-300 bg-orange-50 p-4'>
            <h2 className='flex items-center gap-2 text-base font-semibold text-orange-900'>
              <AlertCircle className='h-5 w-5' />
              Correção solicitada na execução
            </h2>
            {correcao ? (
              <>
                <p className='mt-1 text-sm text-orange-900'>
                  Pedido de <strong>{correcao.solicitante_nome || correcao.solicitante_email}</strong>{' '}
                  (rodada {correcao.rodada}). As seções marcadas em laranja precisam de atenção.
                </p>
                <div className='mt-2 flex flex-wrap gap-2'>
                  {correcao.categorias_rotulos.map(rotulo => (
                    <span
                      key={rotulo}
                      className='rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-800'
                    >
                      {rotulo}
                    </span>
                  ))}
                </div>
                <p className='mt-2 whitespace-pre-line break-words rounded bg-white p-3 text-sm text-gray-800'>
                  {correcao.descricao}
                </p>
              </>
            ) : (
              <p className='mt-1 text-sm text-orange-900'>
                Ajuste o que for necessário. Depois de salvar, volte ao detalhe da GMUD e use
                &quot;Reenviar para execução&quot;.
              </p>
            )}
          </div>
        )}

        {/* 1. O quê */}
        <Secao id='oque' numero={1} destaque={secoesDestacadas.has('oque')}>
          <div>
            <Rotulo obrigatorio htmlFor='titulo'>
              Título
            </Rotulo>
            <input
              id='titulo'
              type='text'
              {...register('titulo', { required: 'Título é obrigatório' })}
              className={classeCampo(!!errors.titulo)}
              placeholder='Resuma a mudança em uma frase'
            />
            <MensagemErro mensagem={errors.titulo?.message} />
          </div>

          <div>
            <Rotulo obrigatorio htmlFor='jira_chamado'>
              Chamado Jira
            </Rotulo>
            <input
              id='jira_chamado'
              type='text'
              {...register('jira_chamado', {
                required: 'Número do chamado do Jira é obrigatório',
                pattern: {
                  value: JIRA_REGEX,
                  message: 'Formato inválido. Use o formato SUS-1234',
                },
                setValueAs: (valor: string) => (valor || '').trim().toUpperCase(),
              })}
              className={`${classeCampo(!!errors.jira_chamado)} max-w-xs font-mono uppercase`}
              placeholder='SUS-1234'
              autoComplete='off'
              maxLength={50}
            />
            <MensagemErro mensagem={errors.jira_chamado?.message} />
          </div>

          <div>
            <Rotulo htmlFor='descricao'>Descrição</Rotulo>
            <textarea
              id='descricao'
              {...register('descricao')}
              rows={4}
              className='input'
              placeholder='Explique o contexto, o que será alterado e o motivo'
            />
          </div>

          <fieldset>
            <legend className='mb-1.5 text-sm font-medium text-gray-700'>Tipo de GMUD</legend>
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
              {OPCOES_TIPO.map(opcao => {
                const selecionada = valores.tipo_gmud === opcao.valor;
                return (
                  <label
                    key={opcao.valor}
                    className={`flex cursor-pointer flex-col rounded-lg border p-3 transition-colors focus-within:ring-2 focus-within:ring-primary-500 ${
                      selecionada ? opcao.cor : 'border-gray-300 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <input type='radio' value={opcao.valor} {...register('tipo_gmud')} className='sr-only' />
                    <span className='text-sm font-medium'>{opcao.rotulo}</span>
                    <span className={`text-xs ${selecionada ? 'opacity-80' : 'text-gray-500'}`}>
                      {opcao.ajuda}
                    </span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          <fieldset>
            <legend className='mb-1.5 text-sm font-medium text-gray-700'>Impacto / Risco</legend>
            <div className='grid grid-cols-2 gap-3 sm:grid-cols-4'>
              {OPCOES_IMPACTO.map(opcao => {
                const selecionada = valores.impacto_risco === opcao.valor;
                return (
                  <label
                    key={opcao.valor}
                    className={`cursor-pointer rounded-lg border p-3 text-center text-sm font-medium transition-colors focus-within:ring-2 focus-within:ring-primary-500 ${
                      selecionada ? opcao.cor : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <input type='radio' value={opcao.valor} {...register('impacto_risco')} className='sr-only' />
                    {opcao.rotulo}
                  </label>
                );
              })}
            </div>
          </fieldset>
        </Secao>

        {/* 2. Quando e quem */}
        <Secao id='quando' numero={2} destaque={secoesDestacadas.has('quando')}>
          <div className='grid grid-cols-1 gap-5 sm:grid-cols-2'>
            <div>
              <Rotulo obrigatorio htmlFor='data_prevista'>
                Data prevista
              </Rotulo>
              <input
                id='data_prevista'
                type='date'
                {...register('data_prevista', { required: 'Data prevista é obrigatória' })}
                className={classeCampo(!!errors.data_prevista)}
              />
              <MensagemErro mensagem={errors.data_prevista?.message} />
            </div>
            <div>
              <Rotulo htmlFor='tempo_execucao'>Tempo de execução</Rotulo>
              <input
                id='tempo_execucao'
                type='text'
                {...register('tempo_execucao')}
                className='input'
                placeholder='Ex.: 2 horas, 1 dia'
              />
            </div>
          </div>

          <div>
            <Rotulo obrigatorio htmlFor='responsavel'>
              Responsável
            </Rotulo>
            <input
              id='responsavel'
              type='text'
              {...register('responsavel', { required: 'Responsável é obrigatório' })}
              className={`${classeCampo(!!errors.responsavel)} ${responsavelFixo ? 'bg-gray-50' : ''}`}
              placeholder='E-mail do responsável'
              readOnly={!!responsavelFixo}
            />
            {responsavelFixo && <Ajuda>Definido automaticamente como o usuário logado.</Ajuda>}
            <MensagemErro mensagem={errors.responsavel?.message} />
          </div>

          <Controller
            name='aprovadores'
            control={control}
            rules={{
              validate: valor => (valor && valor.length > 0) || 'Selecione ao menos um aprovador',
            }}
            render={({ field }) => {
              const selecionados: number[] = (field.value || []).map(Number);
              const alternar = (idAprovador: number) =>
                field.onChange(
                  selecionados.includes(idAprovador)
                    ? selecionados.filter(item => item !== idAprovador)
                    : [...selecionados, idAprovador]
                );
              return (
                <div>
                  <Rotulo obrigatorio>Aprovadores</Rotulo>

                  {selecionados.length > 0 && (
                    <div className='mb-2 flex flex-wrap gap-2'>
                      {selecionados.map(idAprovador => {
                        const aprovador = todosAprovadores.find(item => Number(item.id) === idAprovador);
                        return (
                          <span
                            key={idAprovador}
                            className='inline-flex items-center gap-1 rounded-full bg-primary-50 py-1 pl-3 pr-1.5 text-xs font-medium text-primary-800'
                          >
                            {aprovador?.nome || `Aprovador #${idAprovador}`}
                            <button
                              type='button'
                              onClick={() => alternar(idAprovador)}
                              aria-label={`Remover ${aprovador?.nome || 'aprovador'}`}
                              className='rounded-full p-0.5 hover:bg-primary-100'
                            >
                              <X className='h-3.5 w-3.5' />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}

                  <div className='relative'>
                    <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
                    <input
                      type='search'
                      value={busca}
                      onChange={e => setBusca(e.target.value)}
                      className='input pl-9'
                      placeholder='Buscar aprovador por nome, cargo ou e-mail'
                    />
                  </div>

                  <ul className='mt-2 max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200'>
                    {aprovadoresFiltrados.map(aprovador => {
                      const marcado = selecionados.includes(Number(aprovador.id));
                      return (
                        <li key={aprovador.id}>
                          <label className='flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-gray-50'>
                            <input
                              type='checkbox'
                              checked={marcado}
                              onChange={() => alternar(Number(aprovador.id))}
                              className='h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500'
                            />
                            <span className='min-w-0 text-sm'>
                              <span className='block truncate font-medium text-gray-900'>
                                {aprovador.nome}
                              </span>
                              <span className='block truncate text-xs text-gray-500'>
                                {[aprovador.cargo, aprovador.email].filter(Boolean).join(' · ')}
                              </span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                    {aprovadoresFiltrados.length === 0 && (
                      <li className='px-3 py-4 text-center text-sm text-gray-500'>
                        Nenhum aprovador encontrado.
                      </li>
                    )}
                  </ul>
                  <MensagemErro mensagem={errors.aprovadores?.message as string | undefined} />
                </div>
              );
            }}
          />
        </Secao>

        {/* 3. Como */}
        <Secao id='como' numero={3} destaque={secoesDestacadas.has('como')}>
          <Controller
            name='pipeline_necessario'
            control={control}
            render={({ field }) => (
              <Interruptor
                checked={!!field.value}
                onChange={field.onChange}
                titulo='A execução usa pipeline'
                descricao='Informe o pipeline e a versão que serão executados'
                icone={GitBranch}
              />
            )}
          />
          {valores.pipeline_necessario && (
            <div className='grid grid-cols-1 gap-5 pl-0 sm:grid-cols-2 sm:pl-4'>
              <div>
                <Rotulo htmlFor='nome_pipeline'>Nome do pipeline</Rotulo>
                <input
                  id='nome_pipeline'
                  type='text'
                  {...register('nome_pipeline')}
                  className='input'
                  placeholder='Ex.: production-deploy'
                />
              </div>
              <div>
                <Rotulo htmlFor='versao_pipeline'>Versão</Rotulo>
                <input
                  id='versao_pipeline'
                  type='text'
                  {...register('versao_pipeline')}
                  className='input'
                  placeholder='Ex.: v1.0.0'
                />
              </div>
            </div>
          )}

          <Interruptor
            checked={temScript}
            onChange={alternarScript}
            titulo='Há script de banco de dados'
            descricao='Cole o script e/ou anexe os arquivos (SQL, ZIP, RAR, 7Z ou TXT)'
            icone={Database}
          />
          {temScript && (
            <div className='space-y-4 sm:pl-4'>
              <div>
                <Rotulo htmlFor='script_banco'>Script</Rotulo>
                <textarea
                  id='script_banco'
                  {...register('script_banco')}
                  rows={6}
                  className='input font-mono text-sm'
                  placeholder='Cole aqui o script SQL'
                />
              </div>
              <div>
                <Rotulo>Anexos do script</Rotulo>
                {editando && gmudId ? (
                  <FileUpload
                    gmudId={gmudId}
                    categoria='script_banco'
                    onFilesChange={arquivos => setQtdScriptServidor(arquivos.length)}
                  />
                ) : (
                  <TempFileUpload
                    categoria='script_banco'
                    onFilesChange={setArquivosScript}
                    maxFiles={10}
                    maxSizePerFile={10}
                  />
                )}
              </div>
            </div>
          )}
        </Secao>

        {/* 4. Segurança */}
        <Secao id='seguranca' numero={4} destaque={secoesDestacadas.has('seguranca')}>
          <div>
            <Rotulo paraEnviar htmlFor='plano_rollback'>
              Plano de rollback
            </Rotulo>
            <textarea
              id='plano_rollback'
              {...register('plano_rollback')}
              rows={4}
              className='input'
              placeholder='Como reverter a mudança se algo der errado'
            />
          </div>

          <div className='border-t border-gray-100 pt-5'>
            <h3 className='mb-3 text-sm font-semibold text-gray-900'>Evidências de testes</h3>
            <div className='space-y-5'>
              <div>
                <Rotulo paraEnviar htmlFor='evidencia_testes'>
                  Descrição das evidências
                </Rotulo>
                <textarea
                  id='evidencia_testes'
                  {...register('evidencia_testes')}
                  rows={4}
                  className='input'
                  placeholder='Descreva os testes realizados e seus resultados'
                />
              </div>

              <div>
                <Rotulo htmlFor='evidencia_testes_link'>Link para outras plataformas</Rotulo>
                <input
                  id='evidencia_testes_link'
                  type='url'
                  {...register('evidencia_testes_link')}
                  className='input'
                  placeholder='https://exemplo.com/evidencias'
                />
                <Ajuda>Jira, Confluence, TestRail, Google Drive etc.</Ajuda>
              </div>

              <div>
                <Rotulo paraEnviar>Arquivos de evidência</Rotulo>
                {editando && gmudId ? (
                  <FileUpload
                    gmudId={gmudId}
                    onFilesChange={arquivos => setQtdEvidenciaServidor(arquivos.length)}
                  />
                ) : (
                  <TempFileUpload
                    onFilesChange={setArquivosEvidencia}
                    maxFiles={10}
                    maxSizePerFile={10}
                  />
                )}
              </div>
            </div>
          </div>
        </Secao>

        {/* 5. Revisão */}
        <Secao id='revisao' numero={5} destaque={false}>
          <dl className='grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2'>
            <div className='min-w-0'>
              <dt className='text-xs text-gray-500'>Título</dt>
              <dd className='truncate font-medium text-gray-900'>{valores.titulo || '—'}</dd>
            </div>
            <div>
              <dt className='text-xs text-gray-500'>Chamado Jira</dt>
              <dd className='font-mono font-medium text-gray-900'>
                {(valores.jira_chamado || '').toUpperCase() || '—'}
              </dd>
            </div>
            <div>
              <dt className='text-xs text-gray-500'>Tipo / Impacto</dt>
              <dd className='font-medium capitalize text-gray-900'>
                {valores.tipo_gmud} · {valores.impacto_risco === 'medio' ? 'médio' : valores.impacto_risco === 'critico' ? 'crítico' : valores.impacto_risco}
              </dd>
            </div>
            <div>
              <dt className='text-xs text-gray-500'>Data prevista · Aprovadores</dt>
              <dd className='font-medium text-gray-900'>
                {valores.data_prevista
                  ? valores.data_prevista.split('-').reverse().join('/')
                  : '—'}{' '}
                · {qtdAprovadores}
              </dd>
            </div>
          </dl>

          <div className='rounded-lg border border-gray-200'>
            <p className='border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700'>
              Checklist para enviar à aprovação
            </p>
            <ul className='divide-y divide-gray-100'>
              {itensProntidao.map(item => (
                <li key={item.chave}>
                  <button
                    type='button'
                    onClick={() => irParaSecao(item.secao)}
                    className='flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-gray-50'
                  >
                    {item.ok ? (
                      <CheckCircle2 className='h-4 w-4 flex-shrink-0 text-success-600' />
                    ) : (
                      <Circle className='h-4 w-4 flex-shrink-0 text-amber-500' />
                    )}
                    <span className={item.ok ? 'text-gray-700' : 'font-medium text-gray-900'}>
                      {item.rotulo}
                    </span>
                    {!item.ok && <ChevronRight className='ml-auto h-4 w-4 text-gray-400' />}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <p className='text-xs text-gray-500'>
            O rascunho pode ser salvo a qualquer momento, mesmo com itens pendentes.
          </p>
        </Secao>

        {/* Barra de ações fixa */}
        <div className='sticky bottom-0 z-20 -mx-4 flex flex-col-reverse gap-3 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:mx-0 sm:flex-row sm:items-center sm:justify-end sm:rounded-lg sm:border'>
          <button type='button' onClick={onCancelar} disabled={salvando} className='btn-secondary'>
            Cancelar
          </button>
          <button
            type='submit'
            disabled={salvando}
            className={`${podeEnviarAprovacao ? 'btn-secondary' : 'btn-primary'} inline-flex items-center justify-center gap-2 disabled:opacity-50`}
          >
            <Save className='h-4 w-4' />
            {salvando ? 'Salvando...' : rotuloSalvar}
          </button>
          {podeEnviarAprovacao && (
            <button
              type='button'
              onClick={enviar(true)}
              disabled={salvando || !prontoParaEnviar}
              title={
                prontoParaEnviar
                  ? 'Salva e envia aos aprovadores'
                  : `Falta: ${faltantes.map(item => item.rotulo).join(', ')}`
              }
              className='btn-primary inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50'
            >
              <Send className='h-4 w-4' />
              Salvar e enviar para aprovação
            </button>
          )}
        </div>
      </div>
    </form>
  );
};

export default GMUDForm;
